import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { LogOut, RefreshCw, Info, Search, Trash2 } from 'lucide-react';
import { gradientText } from '../theme';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAdmin } from '../hooks/useAdmin';
import { useAuthContext } from '../contexts/AuthContext';

const AdminUsers: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminRole } = useAdmin();
  const { user } = useAuthContext();
  
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingUser, setDeletingUser] = useState<string | null>(null);
  
  // Vérifier si l'utilisateur est super admin
  const isSuperAdmin = adminRole?.name === 'super_admin';
  
  // Charger la liste des utilisateurs
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setUsers(data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des utilisateurs:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };
  
  // Charger les utilisateurs au chargement de la page
  useEffect(() => {
    loadUsers();
  }, []);
  
  // Filtrer les utilisateurs en fonction du terme de recherche
  const filteredUsers = searchTerm
    ? users.filter(user => 
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : users;
    
  // Fonction pour supprimer un utilisateur
  const handleDeleteUser = async (userId: string) => {
    // Ne pas pouvoir supprimer son propre compte
    if (userId === user?.id) {
      alert("Vous ne pouvez pas supprimer votre propre compte.");
      return;
    }
    
    // Confirmation de suppression
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.")) {
      return;
    }
    
    try {
      setDeletingUser(userId);
      setError(null);
      
      // Appeler d'abord la fonction RPC pour nettoyer les données
      const { error: rpcError } = await supabase.rpc('delete_user', {
        user_id: userId
      });
      
      if (rpcError) throw rpcError;
      
      // Ensuite supprimer l'utilisateur
      const { error } = await supabase.auth.admin.deleteUser(userId);
      
      if (error) throw error;
      
      // Mise à jour de la liste des utilisateurs
      setUsers(users.filter(u => u.id !== userId));
      
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue lors de la suppression');
    } finally {
      setDeletingUser(null);
    }
  };
  
  return (
    <div className="min-h-screen bg-black">
      {/* En-tête */}
      <header className="bg-gradient-to-r from-primary-start/10 to-primary-end/10 border-b border-primary-start/20">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
          <div>
            <h1 className="text-xl font-bold">
              <span className={gradientText}>Gestion des utilisateurs</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadUsers}
              disabled={loading}
              className="text-gray-400 hover:text-white"
              title="Rafraîchir"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">{t('admin.dashboard.returnToDashboard')}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="container mx-auto px-4 py-4 sm:py-6">
        {/* Barre de recherche */}
        <div className="mb-4 sm:mb-6">
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher un utilisateur..."
              className="w-full bg-gray-900/50 border border-gray-700 rounded-lg p-2 sm:p-3 pl-9 sm:pl-10 text-white text-sm sm:text-base"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          </div>
        </div>
        
        {/* Messages d'erreur */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 text-white flex items-start gap-2">
            <Info className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm">{error}</p>
          </div>
        )}
        
        {/* Liste des utilisateurs */}
        <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl overflow-hidden">
          <div className="p-3 sm:p-5 border-b border-primary-start/20">
            <h3 className="text-base sm:text-lg font-medium text-white">Liste des utilisateurs ({filteredUsers.length})</h3>
          </div>
          
          {loading ? (
            <div className="p-6 sm:p-8 flex justify-center">
              <LoadingSpinner size="medium" fullScreen={false} text="Chargement des utilisateurs..." />
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900/50 text-left">
                  <tr>
                    <th className="p-3 sm:p-4 text-xs sm:text-sm text-gray-400 font-medium">Email</th>
                    <th className="p-3 sm:p-4 text-xs sm:text-sm text-gray-400 font-medium hidden sm:table-cell">Nom</th>
                    <th className="p-3 sm:p-4 text-xs sm:text-sm text-gray-400 font-medium hidden sm:table-cell">Pays</th>
                    <th className="p-3 sm:p-4 text-xs sm:text-sm text-gray-400 font-medium hidden sm:table-cell">Ville</th>
                    <th className="p-3 sm:p-4 text-xs sm:text-sm text-gray-400 font-medium hidden sm:table-cell">Inscrit le</th>
                    <th className="p-3 sm:p-4 text-xs sm:text-sm text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-gray-900/30">
                      <td className="p-3 sm:p-4 text-xs sm:text-sm text-white">
                        <div className="flex flex-col sm:hidden mb-1">
                          <span className="text-gray-400 text-xs mb-1">Email:</span>
                        </div>
                        {user.email}
                        
                        {/* Informations supplémentaires visibles seulement sur mobile */}
                        <div className="flex flex-col sm:hidden mt-2 space-y-1 text-xs text-gray-400">
                          <div>
                            <span className="font-medium">Nom:</span> {user.full_name || '-'}
                          </div>
                          <div>
                            <span className="font-medium">Pays:</span> {user.country || '-'}
                          </div>
                          <div>
                            <span className="font-medium">Ville:</span> {user.city || '-'}
                          </div>
                          <div>
                            <span className="font-medium">Inscrit le:</span> {new Date(user.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 sm:p-4 text-xs sm:text-sm text-white hidden sm:table-cell">{user.full_name || '-'}</td>
                      <td className="p-3 sm:p-4 text-xs sm:text-sm text-white hidden sm:table-cell">{user.country || '-'}</td>
                      <td className="p-3 sm:p-4 text-xs sm:text-sm text-white hidden sm:table-cell">{user.city || '-'}</td>
                      <td className="p-3 sm:p-4 text-xs sm:text-sm text-white hidden sm:table-cell">{new Date(user.created_at).toLocaleDateString()}</td>
                      <td className="p-3 sm:p-4">
                        <div className="flex items-center gap-2">
                          {isSuperAdmin && (
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              disabled={deletingUser === user.id}
                              className="text-red-400 hover:text-red-300 disabled:opacity-50"
                              title="Supprimer l'utilisateur"
                            >
                              {deletingUser === user.id ? (
                                <div className="h-5 w-5 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <Trash2 className="w-5 h-5" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 sm:p-8 text-center text-gray-400">
              Aucun utilisateur trouvé
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminUsers; 